import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Settings2, FileEdit, FolderTree, Sparkles, ChevronDown, RotateCcw, CheckSquare, Square, Loader2, FlaskConical, Landmark, TrendingUp, FileText, AlertCircle, Info, Plus, Layout, Eye, Keyboard, Home, User, Plug } from 'lucide-react';
import { ProjectCreationSetup } from './project-creation-setup';
import CategoryManagement from './category-management';
import { FeatureLabsPage } from './feature-labs';
import { SettingsTemplates } from './settings/settings-templates';
import { SettingsIntegrations } from './settings/settings-integrations';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { BudgetTypeSelector } from '@/components/budget-configuration';
import { useBudgetConfiguration } from '@/hooks/use-budget-configuration';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import { fetchProjectCreationSettings, saveProjectCreationSettings } from '@/lib/data-service';
import { useUserPreferences, defaultPreferences } from '@/providers/UserPreferencesProvider';
import { useDemoSession } from '@/providers/DemoSessionProvider';

// Field Constraints types
interface FieldConstraintSettings {
  projectCodeMaxLength: number;
  projectNameMaxLength: number;
  descriptionMaxLength: number;
}

const FIELD_CONSTRAINTS = {
  projectCode: { min: 1, max: 30, default: 10 },
  projectName: { min: 1, max: 80, default: 80 },
  description: { min: 1, max: 80, default: 80 },
};

const STORAGE_KEY = 'integration-settings';

// Field Constraints Content Component
interface FieldConstraintsContentProps {
  fieldSettings: FieldConstraintSettings;
  setFieldSettings: React.Dispatch<React.SetStateAction<FieldConstraintSettings>>;
  fieldErrors: Record<string, string>;
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

function FieldConstraintsContent({
  fieldSettings,
  setFieldSettings,
  fieldErrors,
  setFieldErrors,
}: FieldConstraintsContentProps) {
  const handleFieldChange = (field: keyof FieldConstraintSettings, value: string) => {
    const numValue = parseInt(value, 10);
    setFieldSettings(prev => ({
      ...prev,
      [field]: isNaN(numValue) ? 0 : numValue,
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="max-w-5xl mx-auto p-6 space-y-6 pb-32">
          <Card>
            <CardHeader>
              <CardTitle>Field Length Constraints</CardTitle>
              <CardDescription>
                Configure maximum character lengths for project creation fields
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Project Code */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="projectCodeMaxLength">
                    Project Code Maximum Length
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Range: {FIELD_CONSTRAINTS.projectCode.min} - {FIELD_CONSTRAINTS.projectCode.max} characters (database limit: VARCHAR(30))</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-1">
                    <Input
                      id="projectCodeMaxLength"
                      type="number"
                      min={FIELD_CONSTRAINTS.projectCode.min}
                      max={FIELD_CONSTRAINTS.projectCode.max}
                      value={fieldSettings.projectCodeMaxLength}
                      onChange={(e) => handleFieldChange('projectCodeMaxLength', e.target.value)}
                      className={fieldErrors.projectCodeMaxLength ? 'border-destructive' : ''}
                    />
                    {fieldErrors.projectCodeMaxLength && (
                      <p className="text-sm text-destructive">{fieldErrors.projectCodeMaxLength}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Name */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="projectNameMaxLength">
                    Project Name Maximum Length
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Range: {FIELD_CONSTRAINTS.projectName.min} - {FIELD_CONSTRAINTS.projectName.max} characters (database limit: VARCHAR(80))</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-1">
                    <Input
                      id="projectNameMaxLength"
                      type="number"
                      min={FIELD_CONSTRAINTS.projectName.min}
                      max={FIELD_CONSTRAINTS.projectName.max}
                      value={fieldSettings.projectNameMaxLength}
                      onChange={(e) => handleFieldChange('projectNameMaxLength', e.target.value)}
                      className={fieldErrors.projectNameMaxLength ? 'border-destructive' : ''}
                    />
                    {fieldErrors.projectNameMaxLength && (
                      <p className="text-sm text-destructive">{fieldErrors.projectNameMaxLength}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="descriptionMaxLength">
                    Description Maximum Length
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Range: {FIELD_CONSTRAINTS.description.min} - {FIELD_CONSTRAINTS.description.max} characters (database limit: VARCHAR(80))</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-1">
                    <Input
                      id="descriptionMaxLength"
                      type="number"
                      min={FIELD_CONSTRAINTS.description.min}
                      max={FIELD_CONSTRAINTS.description.max}
                      value={fieldSettings.descriptionMaxLength}
                      onChange={(e) => handleFieldChange('descriptionMaxLength', e.target.value)}
                      className={fieldErrors.descriptionMaxLength ? 'border-destructive' : ''}
                    />
                    {fieldErrors.descriptionMaxLength && (
                      <p className="text-sm text-destructive">{fieldErrors.descriptionMaxLength}</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
}

// Navigation items for the landing page dropdown
const landingPageOptions = [
  { id: 'dashboard', name: 'Homepage (default)' },
  { id: 'projects', name: 'Projects' },
  { id: 'portfolio', name: 'Portfolio' },
  { id: 'directory', name: 'Directory' },
  { id: 'roles-rates', name: 'Roles & Rates' },
  { id: 'resources', name: 'Resource Allocation' },
  { id: 'utilisation', name: 'Utilisation' },
  { id: 'wip-reporting', name: 'WIP Reporting' },
  { id: 'profitability', name: 'Profitability' },
  { id: 'budgets', name: 'Budgets & Variance' },
  { id: 'gantt', name: 'Gantt Charts' },
];

// Helper function to get greeting based on time of day
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 18) return 'Good Afternoon';
  return 'Good Evening';
};

// User Preferences Content Component
interface UserPreferencesContentProps {
  localSidebarPosition: 'left' | 'right';
  setLocalSidebarPosition: (value: 'left' | 'right') => void;
  localShowWelcomeDialog: boolean;
  setLocalShowWelcomeDialog: (value: boolean) => void;
  localDefaultLandingPage: string;
  setLocalDefaultLandingPage: (value: string) => void;
  localRememberLastPage: boolean;
  setLocalRememberLastPage: (value: boolean) => void;
  localDisplayDensity: 'comfortable' | 'compact';
  setLocalDisplayDensity: (value: 'comfortable' | 'compact') => void;
  localKeyboardShortcutsEnabled: boolean;
  setLocalKeyboardShortcutsEnabled: (value: boolean) => void;
  userName: string;
}

function UserPreferencesContent({
  localSidebarPosition,
  setLocalSidebarPosition,
  localShowWelcomeDialog,
  setLocalShowWelcomeDialog,
  localDefaultLandingPage,
  setLocalDefaultLandingPage,
  localRememberLastPage,
  setLocalRememberLastPage,
  localDisplayDensity,
  setLocalDisplayDensity,
  localKeyboardShortcutsEnabled,
  setLocalKeyboardShortcutsEnabled,
  userName,
}: UserPreferencesContentProps) {
  return (
    <div className="max-w-5xl mx-auto space-y-8 p-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">{getGreeting()}, {userName}</h2>
        <p className="text-muted-foreground">
          Customise Projects & Billing to fit your preferences
        </p>
      </div>

      {/* Appearance Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Layout className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Appearance</h3>
            <p className="text-sm text-muted-foreground">Customize the look and feel of your interface</p>
          </div>
        </div>
        
        <div className="rounded-lg border bg-card">
          <div className="divide-y">
            {/* 
              HIDDEN: Sidebar Position Toggle
              
              Product Decision: This feature has been temporarily hidden from the UI.
              
              Reason: There is a known bug where saving the sidebar position to 'right' 
              has no effect - the sidebar always remains on the left regardless of the 
              saved preference. Rather than removing the code entirely, we've hidden the 
              UI element so it can be easily restored if/when this feature is properly 
              supported in the future.
              
              The underlying code and state management (localSidebarPosition, 
              setLocalSidebarPosition, useUserPreferences hook) remain intact.
              
              To restore: Uncomment the JSX block below.
              
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="sidebar-position" className="text-sm font-medium">
                    Sidebar Position
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Choose which side the navigation sidebar appears
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${localSidebarPosition === 'left' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Left
                  </span>
                  <Switch
                    id="sidebar-position"
                    checked={localSidebarPosition === 'right'}
                    onCheckedChange={(checked) => setLocalSidebarPosition(checked ? 'right' : 'left')}
                  />
                  <span className={`text-xs ${localSidebarPosition === 'right' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Right
                  </span>
                </div>
              </div>
            */}

            {/* Display Density */}
            <div className="flex items-center justify-between p-4">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="display-density" className="text-sm font-medium">
                  Display Density
                </Label>
                <p className="text-xs text-muted-foreground">
                  Adjust spacing and sizing for your preferred information density
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${localDisplayDensity === 'comfortable' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                  Comfortable
                </span>
                <Switch
                  id="display-density"
                  checked={localDisplayDensity === 'compact'}
                  onCheckedChange={(checked) => setLocalDisplayDensity(checked ? 'compact' : 'comfortable')}
                />
                <span className={`text-xs ${localDisplayDensity === 'compact' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                  Compact
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 
        HIDDEN: Navigation Section (Default Landing Page & Remember Last Page)
        
        Product Decision: This entire section has been temporarily hidden from the UI.
        
        Reason: Both the "Default Landing Page" and "Remember Last Visited Page" options
        are believed to not be working correctly. Rather than removing the code entirely,
        we've hidden the UI elements so they can be easily restored if/when these features
        are properly supported in the future.
        
        The underlying code and state management (localDefaultLandingPage, 
        setLocalDefaultLandingPage, localRememberLastPage, setLocalRememberLastPage,
        landingPageOptions, useUserPreferences hook) remain intact.
        
        To restore: Uncomment the JSX block below.
        
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Home className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Navigation</h3>
              <p className="text-sm text-muted-foreground">Control your navigation and startup behaviour</p>
            </div>
          </div>
          
          <div className="rounded-lg border bg-card">
            <div className="divide-y">
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="landing-page" className="text-sm font-medium">
                    Default Landing Page
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Choose which page opens when you log in
                  </p>
                </div>
                <Select
                  value={localDefaultLandingPage}
                  onValueChange={setLocalDefaultLandingPage}
                >
                  <SelectTrigger id="landing-page" className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {landingPageOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="remember-page" className="text-sm font-medium cursor-pointer">
                    Remember Last Visited Page
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically return to your last visited page on login (overrides default landing page)
                  </p>
                </div>
                <Checkbox
                  id="remember-page"
                  checked={localRememberLastPage}
                  onCheckedChange={(checked) => setLocalRememberLastPage(checked === true)}
                />
              </div>
            </div>
          </div>
        </section>
      */}

      {/* Accessibility Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Keyboard className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Accessibility</h3>
            <p className="text-sm text-muted-foreground">Keyboard shortcuts and interaction preferences</p>
          </div>
        </div>
        
        <div className="rounded-lg border bg-card">
          <div className="divide-y">
            {/* Keyboard Shortcuts */}
            <div className="flex items-center justify-between p-4">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="keyboard-shortcuts" className="text-sm font-medium cursor-pointer">
                  Keyboard Shortcuts
                </Label>
                <p className="text-xs text-muted-foreground">
                  Enable keyboard shortcuts for faster navigation and accessibility
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Press <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">Ctrl+/</kbd> to view all shortcuts
                </p>
              </div>
              <Checkbox
                id="keyboard-shortcuts"
                checked={localKeyboardShortcutsEnabled}
                onCheckedChange={(checked) => setLocalKeyboardShortcutsEnabled(checked === true)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* General Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Eye className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">General</h3>
            <p className="text-sm text-muted-foreground">General application preferences</p>
          </div>
        </div>
        
        <div className="rounded-lg border bg-card">
          <div className="divide-y">
            {/* Welcome Dialog */}
            <div className="flex items-center justify-between p-4">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="show-welcome" className="text-sm font-medium cursor-pointer">
                  Show Welcome Dialog
                </Label>
                <p className="text-xs text-muted-foreground">
                  Display the welcome message when the application starts
                </p>
              </div>
              <Checkbox
                id="show-welcome"
                checked={localShowWelcomeDialog}
                onCheckedChange={(checked) => setLocalShowWelcomeDialog(checked === true)}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function SettingsPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'user-preferences');

  // ===== FIELD CONSTRAINTS STATE =====
  const [fieldSettings, setFieldSettings] = useState<FieldConstraintSettings>({
    projectCodeMaxLength: FIELD_CONSTRAINTS.projectCode.default,
    projectNameMaxLength: FIELD_CONSTRAINTS.projectName.default,
    descriptionMaxLength: FIELD_CONSTRAINTS.description.default,
  });
  const [savedFieldSettings, setSavedFieldSettings] = useState<FieldConstraintSettings>({
    projectCodeMaxLength: FIELD_CONSTRAINTS.projectCode.default,
    projectNameMaxLength: FIELD_CONSTRAINTS.projectName.default,
    descriptionMaxLength: FIELD_CONSTRAINTS.description.default,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSavingFields, setIsSavingFields] = useState(false);

  // ===== PROJECT CREATION STATE (from context) =====
  const {
    hasUnsavedChanges: hasProjectCreationChanges,
    saveChanges: saveProjectCreationChanges,
    resetChanges: resetProjectCreationChanges,
    resetToDefaults: resetProjectCreationToDefaults,
    makeAllRequired,
    makeAllOptional,
  } = useFieldConfig();

  // ===== BUDGET CATEGORIES STATE =====
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  // ===== BUDGET CONFIGURATION STATE =====
  const {
    trackingType,
    hasUnsavedChanges: hasBudgetConfigChanges,
    isSaving: isSavingBudgetConfig,
    updateTrackingType,
    saveConfiguration: saveBudgetConfiguration,
    discardChanges: discardBudgetConfigChanges,
  } = useBudgetConfiguration();
  const [showBudgetConfigSuccess, setShowBudgetConfigSuccess] = useState(false);

  // ===== USER PREFERENCES STATE =====
  const { preferences, updatePreferences } = useUserPreferences();
  const { userName } = useDemoSession();
  const [localSidebarPosition, setLocalSidebarPosition] = useState(preferences.sidebarPosition);
  const [localShowWelcomeDialog, setLocalShowWelcomeDialog] = useState(preferences.showWelcomeDialog);
  const [localDefaultLandingPage, setLocalDefaultLandingPage] = useState(preferences.defaultLandingPage);
  const [localRememberLastPage, setLocalRememberLastPage] = useState(preferences.rememberLastPage);
  const [localDisplayDensity, setLocalDisplayDensity] = useState(preferences.displayDensity);
  const [localKeyboardShortcutsEnabled, setLocalKeyboardShortcutsEnabled] = useState(preferences.keyboardShortcutsEnabled);

  // Load field settings on mount
  useEffect(() => {
    loadFieldSettings();
  }, []);

  const loadFieldSettings = async () => {
    try {
      // Try to load from API first
      const apiSettings = await fetchProjectCreationSettings();
      if (apiSettings?.fieldLengthConstraints) {
        const parsed = JSON.parse(apiSettings.fieldLengthConstraints);
        setFieldSettings(parsed);
        setSavedFieldSettings(parsed);
        // Update localStorage to keep it in sync
        localStorage.setItem(STORAGE_KEY, apiSettings.fieldLengthConstraints);
        return;
      }
      
      // Fall back to localStorage if API returns null (demo mode or no settings)
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFieldSettings(parsed);
        setSavedFieldSettings(parsed);
      }
    } catch (error) {
      console.error('Error loading field settings:', error);
      // Fall back to localStorage on error
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setFieldSettings(parsed);
          setSavedFieldSettings(parsed);
        }
      } catch {
        toast.error('Failed to load field settings.');
      }
    }
  };

  // ===== FIELD CONSTRAINTS HANDLERS =====
  const hasUnsavedFieldChanges = 
    fieldSettings.projectCodeMaxLength !== savedFieldSettings.projectCodeMaxLength ||
    fieldSettings.projectNameMaxLength !== savedFieldSettings.projectNameMaxLength ||
    fieldSettings.descriptionMaxLength !== savedFieldSettings.descriptionMaxLength;

  const validateField = (field: keyof typeof FIELD_CONSTRAINTS, value: number): string | null => {
    const constraints = FIELD_CONSTRAINTS[field];
    
    if (isNaN(value)) {
      return 'Please enter a valid number';
    }
    
    if (value < constraints.min) {
      return `Minimum value is ${constraints.min}`;
    }
    
    if (value > constraints.max) {
      return `Maximum value is ${constraints.max} (database limit)`;
    }
    
    return null;
  };

  const handleSaveFieldSettings = async () => {
    // Validate all fields
    const newErrors: Record<string, string> = {};
    
    Object.entries({
      projectCodeMaxLength: 'projectCode',
      projectNameMaxLength: 'projectName',
      descriptionMaxLength: 'description',
    }).forEach(([settingKey, constraintKey]) => {
      const error = validateField(
        constraintKey as keyof typeof FIELD_CONSTRAINTS,
        fieldSettings[settingKey as keyof FieldConstraintSettings]
      );
      if (error) {
        newErrors[settingKey] = error;
      }
    });
    
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      toast.error('Please fix the errors before saving.');
      return;
    }
    
    try {
      setIsSavingFields(true);
      const fieldLengthConstraintsJson = JSON.stringify(fieldSettings);
      
      // Save to localStorage first for immediate feedback
      localStorage.setItem(STORAGE_KEY, fieldLengthConstraintsJson);
      
      // Fetch current settings to preserve other fields (fieldConfigs, timelineSettings)
      const currentSettings = await fetchProjectCreationSettings();
      
      // Save to API (in live mode), preserving existing settings
      await saveProjectCreationSettings({
        fieldConfigs: currentSettings?.fieldConfigs ?? null,
        timelineSettings: currentSettings?.timelineSettings ?? null,
        fieldLengthConstraints: fieldLengthConstraintsJson,
      });
      
      setSavedFieldSettings(fieldSettings);
      // Dispatch custom event to notify other components
      window.dispatchEvent(new StorageEvent('storage', {
        key: STORAGE_KEY,
        newValue: fieldLengthConstraintsJson,
        storageArea: localStorage,
      }));
      toast.success('Field settings saved successfully.');
    } catch (error) {
      console.error('Error saving field settings:', error);
      toast.error('Failed to save field settings.');
    } finally {
      setIsSavingFields(false);
    }
  };

  const handleDiscardFieldSettings = () => {
    setFieldSettings(savedFieldSettings);
    setFieldErrors({});
    toast.info('Changes discarded', {
      description: 'Reverted to last saved settings.',
    });
  };

  const handleResetFieldSettingsToDefaults = () => {
    const defaults: FieldConstraintSettings = {
      projectCodeMaxLength: FIELD_CONSTRAINTS.projectCode.default,
      projectNameMaxLength: FIELD_CONSTRAINTS.projectName.default,
      descriptionMaxLength: FIELD_CONSTRAINTS.description.default,
    };
    setFieldSettings(defaults);
    setFieldErrors({});
    toast.info('Reset to defaults', {
      description: 'All settings have been reset to their default values.',
    });
  };

  // ===== PROJECT CREATION HANDLERS =====
  const handleSaveProjectCreation = () => {
    saveProjectCreationChanges();
    toast.success('Settings saved successfully', {
      description: 'Your project creation setup has been updated.',
    });
  };

  const handleResetProjectCreation = () => {
    resetProjectCreationChanges();
    toast.info('Changes discarded', {
      description: 'Reverted to last saved settings.',
    });
  };

  const handleResetProjectCreationToDefaults = () => {
    resetProjectCreationToDefaults();
    toast.info('Reset to defaults', {
      description: 'All settings have been reset to their default values.',
    });
  };

  const handleMakeAllRequired = () => {
    makeAllRequired();
    toast.info('All fields set to required', {
      description: 'All fields are now marked as required.',
    });
  };

  const handleMakeAllOptional = () => {
    makeAllOptional();
    toast.info('All fields set to optional', {
      description: 'All fields (except essential) are now optional.',
    });
  };

  // ===== BUDGET CATEGORIES HANDLERS =====
  const handleAddCategory = () => {
    toast.info('Add Category feature coming soon!');
  };

  // ===== BUDGET CONFIGURATION HANDLERS =====
  const handleBudgetTypeChange = (type: any) => {
    updateTrackingType(type);
  };

  const handleSaveBudgetConfig = async () => {
    const result = await saveBudgetConfiguration();
    if (result.success) {
      setShowBudgetConfigSuccess(true);
      setTimeout(() => setShowBudgetConfigSuccess(false), 3000);
      toast.success('Budget configuration saved successfully!');
    } else {
      toast.error('Failed to save budget configuration.');
    }
  };

  const handleDiscardBudgetConfig = () => {
    discardBudgetConfigChanges();
    toast.info('Changes discarded', {
      description: 'Reverted to last saved configuration.',
    });
  };

  // ===== USER PREFERENCES HANDLERS =====
  const hasUnsavedUserPreferencesChanges = 
    localSidebarPosition !== preferences.sidebarPosition ||
    localShowWelcomeDialog !== preferences.showWelcomeDialog ||
    localDefaultLandingPage !== preferences.defaultLandingPage ||
    localRememberLastPage !== preferences.rememberLastPage ||
    localDisplayDensity !== preferences.displayDensity ||
    localKeyboardShortcutsEnabled !== preferences.keyboardShortcutsEnabled;

  const handleSaveUserPreferences = () => {
    updatePreferences({
      sidebarPosition: localSidebarPosition,
      showWelcomeDialog: localShowWelcomeDialog,
      defaultLandingPage: localDefaultLandingPage,
      rememberLastPage: localRememberLastPage,
      displayDensity: localDisplayDensity,
      keyboardShortcutsEnabled: localKeyboardShortcutsEnabled,
    });
    toast.success('Preferences saved successfully');
  };

  const handleDiscardUserPreferences = () => {
    setLocalSidebarPosition(preferences.sidebarPosition);
    setLocalShowWelcomeDialog(preferences.showWelcomeDialog);
    setLocalDefaultLandingPage(preferences.defaultLandingPage);
    setLocalRememberLastPage(preferences.rememberLastPage);
    setLocalDisplayDensity(preferences.displayDensity);
    setLocalKeyboardShortcutsEnabled(preferences.keyboardShortcutsEnabled);
    toast.info('Changes discarded', {
      description: 'Reverted to last saved preferences.',
    });
  };

  const handleResetUserPreferencesToDefaults = () => {
    updatePreferences(defaultPreferences);
    setLocalSidebarPosition(defaultPreferences.sidebarPosition);
    setLocalShowWelcomeDialog(defaultPreferences.showWelcomeDialog);
    setLocalDefaultLandingPage(defaultPreferences.defaultLandingPage);
    setLocalRememberLastPage(defaultPreferences.rememberLastPage);
    setLocalDisplayDensity(defaultPreferences.displayDensity);
    setLocalKeyboardShortcutsEnabled(defaultPreferences.keyboardShortcutsEnabled);
    toast.info('Reset to defaults', {
      description: 'All preferences have been reset to their default values.',
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Content Header Bar 2 */}
      <div className="sticky top-0 z-40 bg-background border-b shadow-sm">
        {/* Tabs Row */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="border-b bg-muted/30">
            <TabsList variant="icon" showArrows>
              <TabsTrigger variant="icon" value="user-preferences" showUnderline>
                <User className="size-5" />
                User Preferences
              </TabsTrigger>
              <TabsTrigger variant="icon" value="project-creation" showUnderline>
                <FileEdit className="size-5" />
                Project Creation
              </TabsTrigger>
              <TabsTrigger variant="icon" value="field-constraints" showUnderline>
                <Settings2 className="size-5" />
                Field Constraints
              </TabsTrigger>
              <TabsTrigger variant="icon" value="templates" showUnderline>
                <FolderTree className="size-5" />
                Templates
              </TabsTrigger>
              <TabsTrigger variant="icon" value="budget-settings" showUnderline>
                <Landmark className="size-5" />
                Budget Settings
              </TabsTrigger>
              <TabsTrigger variant="icon" value="thresholds" showUnderline>
                <TrendingUp className="size-5" />
                Thresholds
              </TabsTrigger>
              <TabsTrigger variant="icon" value="integrations" showUnderline>
                <Plug className="size-5" />
                Integrations
              </TabsTrigger>
              <TabsTrigger variant="icon" value="general" showUnderline>
                <Settings2 className="size-5" />
                General
              </TabsTrigger>
              <TabsTrigger variant="icon" value="feature-labs" showUnderline>
                <FlaskConical className="size-5" />
                <Badge variant="outline" className="ml-1.5">Feature Labs</Badge>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab-Specific Controls Row */}
          <SettingsTabControls 
            activeTab={activeTab}
            // Field Constraints props
            hasUnsavedFieldChanges={hasUnsavedFieldChanges}
            isSavingFields={isSavingFields}
            onSaveFieldSettings={handleSaveFieldSettings}
            onDiscardFieldSettings={handleDiscardFieldSettings}
            onResetFieldSettingsToDefaults={handleResetFieldSettingsToDefaults}
            fieldErrors={fieldErrors}
            // Project Creation props
            hasProjectCreationChanges={hasProjectCreationChanges}
            onSaveProjectCreation={handleSaveProjectCreation}
            onResetProjectCreation={handleResetProjectCreation}
            onResetProjectCreationToDefaults={handleResetProjectCreationToDefaults}
            onMakeAllRequired={handleMakeAllRequired}
            onMakeAllOptional={handleMakeAllOptional}
            // Budget Categories props
            onAddCategory={handleAddCategory}
            // User Preferences props
            hasUnsavedUserPreferencesChanges={hasUnsavedUserPreferencesChanges}
            onSaveUserPreferences={handleSaveUserPreferences}
            onDiscardUserPreferences={handleDiscardUserPreferences}
            onResetUserPreferencesToDefaults={handleResetUserPreferencesToDefaults}
          />
        </Tabs>
      </div>

      {/* Scrollable Tab Contents */}
      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="user-preferences" className="mt-0">
            <UserPreferencesContent
              localSidebarPosition={localSidebarPosition}
              setLocalSidebarPosition={setLocalSidebarPosition}
              localShowWelcomeDialog={localShowWelcomeDialog}
              setLocalShowWelcomeDialog={setLocalShowWelcomeDialog}
              localDefaultLandingPage={localDefaultLandingPage}
              setLocalDefaultLandingPage={setLocalDefaultLandingPage}
              localRememberLastPage={localRememberLastPage}
              setLocalRememberLastPage={setLocalRememberLastPage}
              localDisplayDensity={localDisplayDensity}
              setLocalDisplayDensity={setLocalDisplayDensity}
              localKeyboardShortcutsEnabled={localKeyboardShortcutsEnabled}
              setLocalKeyboardShortcutsEnabled={setLocalKeyboardShortcutsEnabled}
              userName={userName}
            />
          </TabsContent>

          <TabsContent value="project-creation" className="mt-0">
            <ProjectCreationSetup />
          </TabsContent>

          <TabsContent value="field-constraints" className="mt-0">
            <FieldConstraintsContent 
              fieldSettings={fieldSettings}
              setFieldSettings={setFieldSettings}
              fieldErrors={fieldErrors}
              setFieldErrors={setFieldErrors}
            />
          </TabsContent>

          <TabsContent value="templates" className="mt-0">
            <SettingsTemplates />
          </TabsContent>

          <TabsContent value="budget-settings" className="mt-0">
            <div className="max-w-5xl mx-auto p-6 space-y-6">
              {/* Budget Configuration Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Budget Tracking Configuration</CardTitle>
                  <CardDescription>
                    Configure how budgets are tracked across all projects. This setting applies globally to Resource, Sales, and Cost budgets.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Success Message */}
                  {showBudgetConfigSuccess && (
                    <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                      <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertTitle className="text-green-800 dark:text-green-200">Configuration Saved</AlertTitle>
                      <AlertDescription className="text-green-700 dark:text-green-300">
                        Your budget tracking configuration has been updated successfully.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Unsaved Changes Warning */}
                  {hasBudgetConfigChanges && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        You have unsaved changes. Click "Save Configuration" to apply your changes.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Budget Type Selector */}
                  <BudgetTypeSelector value={trackingType} onChange={handleBudgetTypeChange} />

                  {/* Action Buttons */}
                  {hasBudgetConfigChanges && (
                    <div className="flex items-center justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={handleDiscardBudgetConfig}
                        disabled={isSavingBudgetConfig}
                      >
                        Discard Changes
                      </Button>
                      <Button
                        onClick={handleSaveBudgetConfig}
                        disabled={isSavingBudgetConfig}
                      >
                        {isSavingBudgetConfig ? 'Saving...' : 'Save Configuration'}
                      </Button>
                    </div>
                  )}

                  {/* Info Alert */}
                  <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
                    <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <AlertDescription className="text-blue-900 dark:text-blue-100">
                      <strong>Global Configuration:</strong> This setting applies to all budget types (Resource, Sales, and Cost) 
                      to ensure consistent tracking and reporting across your projects.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Budget Categories Section */}
              <div>
                <div className="mb-4">
                  <h2 className="text-2xl font-bold tracking-tight">Budget Categories</h2>
                  <p className="text-muted-foreground mt-1">
                    Manage budget categories used across your projects
                  </p>
                </div>
                <CategoryManagement 
                  searchTerm={categorySearchTerm}
                  setSearchTerm={setCategorySearchTerm}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="thresholds" className="mt-0">
            <div className="max-w-5xl mx-auto p-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12 space-y-4">
                    <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Threshold Settings</h3>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        Configure project thresholds and limits. This feature will be available soon.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="integrations" className="mt-0">
            <SettingsIntegrations />
          </TabsContent>

          <TabsContent value="general" className="mt-0">
            <div className="max-w-5xl mx-auto p-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12 space-y-4">
                    <Settings2 className="h-12 w-12 text-muted-foreground mx-auto" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">General Settings</h3>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        General configuration options will be available here as new features are developed.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="feature-labs" className="mt-0">
            <div className="max-w-5xl mx-auto p-6">
              <FeatureLabsPage showHeader={false} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

    </div>
  );
}

// Component to render tab-specific controls
interface SettingsTabControlsProps {
  activeTab: string;
  // Field Constraints
  hasUnsavedFieldChanges: boolean;
  isSavingFields: boolean;
  onSaveFieldSettings: () => void;
  onDiscardFieldSettings: () => void;
  onResetFieldSettingsToDefaults: () => void;
  fieldErrors: Record<string, string>;
  // Project Creation
  hasProjectCreationChanges: boolean;
  onSaveProjectCreation: () => void;
  onResetProjectCreation: () => void;
  onResetProjectCreationToDefaults: () => void;
  onMakeAllRequired: () => void;
  onMakeAllOptional: () => void;
  // Budget Categories
  onAddCategory: () => void;
  // User Preferences
  hasUnsavedUserPreferencesChanges: boolean;
  onSaveUserPreferences: () => void;
  onDiscardUserPreferences: () => void;
  onResetUserPreferencesToDefaults: () => void;
}

function SettingsTabControls({
  activeTab,
  hasUnsavedFieldChanges,
  isSavingFields,
  onSaveFieldSettings,
  onDiscardFieldSettings,
  onResetFieldSettingsToDefaults,
  fieldErrors,
  hasProjectCreationChanges,
  onSaveProjectCreation,
  onResetProjectCreation,
  onResetProjectCreationToDefaults,
  onMakeAllRequired,
  onMakeAllOptional,
  onAddCategory,
  hasUnsavedUserPreferencesChanges,
  onSaveUserPreferences,
  onDiscardUserPreferences,
  onResetUserPreferencesToDefaults,
}: SettingsTabControlsProps) {
  return (
    <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-5xl mx-auto flex justify-between items-center gap-3 p-4">
        {activeTab === 'field-constraints' && (
          <>
            {/* Bulk Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  Bulk Actions
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={onResetFieldSettingsToDefaults}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset to Defaults
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Save/Discard Actions */}
            <div className="flex items-center gap-3 ml-auto">
              <Button 
                variant="outline" 
                onClick={onDiscardFieldSettings}
                disabled={!hasUnsavedFieldChanges}
              >
                Discard Changes
              </Button>
              <Button 
                onClick={onSaveFieldSettings}
                disabled={!hasUnsavedFieldChanges || isSavingFields || Object.keys(fieldErrors).length > 0}
              >
                {isSavingFields ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </>
        )}
        
        {activeTab === 'project-creation' && (
          <>
            {/* Bulk Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  Bulk Actions
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={onMakeAllRequired}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Make All Required
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onMakeAllOptional}>
                  <Square className="mr-2 h-4 w-4" />
                  Make All Optional
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onResetProjectCreationToDefaults}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset to Defaults
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Save/Discard Actions */}
            <div className="flex items-center gap-3 ml-auto">
              <Button 
                variant="outline" 
                onClick={onResetProjectCreation}
                disabled={!hasProjectCreationChanges}
              >
                Discard Changes
              </Button>
              <Button 
                onClick={onSaveProjectCreation}
                disabled={!hasProjectCreationChanges}
              >
                Save Changes
              </Button>
            </div>
          </>
        )}
        
        {activeTab === 'budget-categories' && (
          <div className="flex justify-end items-center gap-3 w-full">
            <Button onClick={onAddCategory}>
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </Button>
          </div>
        )}
        
        {activeTab === 'templates' && (
          <div className="flex justify-end items-center gap-3 w-full">
            <Button onClick={() => window.dispatchEvent(new CustomEvent('create-template'))}>
              <Plus className="mr-2 h-4 w-4" />
              Create Template
            </Button>
          </div>
        )}
        
        {activeTab === 'integrations' && (
          <div className="w-full h-12" /> /* Empty space - Integrations has its own internal controls */
        )}
        
        {activeTab === 'feature-labs' && (
          <div className="w-full h-12" /> /* Empty space - Feature Labs has its own internal controls */
        )}
        
        {activeTab === 'future-settings' && (
          <div className="w-full h-12" /> /* Empty space for future settings */
        )}
        
        {activeTab === 'user-preferences' && (
          <>
            <Button 
              variant="outline" 
              onClick={onResetUserPreferencesToDefaults}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to Default
            </Button>

            <div className="flex items-center gap-3 ml-auto">
              <Button 
                variant="outline" 
                onClick={onDiscardUserPreferences}
                disabled={!hasUnsavedUserPreferencesChanges}
              >
                Discard
              </Button>
              <Button 
                onClick={onSaveUserPreferences}
                disabled={!hasUnsavedUserPreferencesChanges}
              >
                Save Changes
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
